import React, {
  useRef,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  X,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Trash2,
  Users,
  Wand2,
  Edit3,
  Download,
  FileText,
} from "lucide-react";
import { Button } from "@core/index";
import { usePodcastPlayer } from "../context/PodcastPlayerContext";
import DeleteConfirmPopUp from "@features/tools/components/tool_components/DeleteConfirmPopUp";
import { useToast } from "@packages/contexts/ToastContext";
// import { useAppSelector } from "@packages/store/hooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetUserPodcastsQueryKey,
  useDeletePodcast,
  useUpdatePodcast,
} from "@packages/api/madlenAPI";
import { CreatePodcastFromReferenceRequestDtoGenerationType } from "@packages/api/madlenAPI.schemas";
import { useTranslation } from "react-i18next";
import PodcastAssignmentModal from "./PodcastAssignmentModal";

// Types
interface WordTiming {
  word: string;
  start: number;
  end: number;
}

interface TranscriptLine {
  start: number; // in milliseconds
  end: number; // in milliseconds
  duration?: number; // in milliseconds, optional for explicit duration tracking
  text: string;
  speakerName?: string;
  speakerId?: number;
  wordTimings?: WordTiming[];
}

interface TranscriptSection {
  lines: TranscriptLine[];
}

interface TransformedTranscript {
  sections: TranscriptSection[];
}

interface PodcastTranscriptItem {
  message?: string;
  displayName?: string;
  id?: number;
  startTimeSeconds?: number;
}

interface PodcastResponseDto {
  id?: number;
  title?: string;
  authorName?: string;
  imageUrl?: string;
  url?: string;
  transcript?: PodcastTranscriptItem[];
}

interface PodcastPlayerModalProps {
  podcast: PodcastResponseDto;
  onClose: () => void;
  onRegenClick?: (
    podcastId: number,
    generationType: CreatePodcastFromReferenceRequestDtoGenerationType,
  ) => void;
  podcastGenerationType?: CreatePodcastFromReferenceRequestDtoGenerationType;
  openPdfCustomizationModal?: (podcastId?: number) => void;
  initialTimeMs?: number;
  autoPlay?: boolean;
}

// Helper function to estimate word timings within a line
const calculateWordTimings = (line: TranscriptLine): WordTiming[] => {
  const words = line.text.split(" ").filter((word) => word.length > 0);
  const totalDuration = line.end - line.start;

  if (words.length === 0) {
    return [];
  }

  // Calculate duration for each word based on character count
  const totalChars = words.reduce((sum, word) => sum + word.length, 0);
  let currentTime = line.start;

  return words.map((word) => {
    // Distribute duration proportionally by character count, leaving small gaps
    const wordDuration = (word.length / totalChars) * totalDuration * 0.95;
    const pauseDuration = (totalDuration * 0.05) / (words.length - 1 || 1);

    const wordTiming = {
      word,
      start: currentTime,
      end: currentTime + wordDuration,
    };

    currentTime += wordDuration + pauseDuration;
    return wordTiming;
  });
};

// Helper function to transform transcript to timed format
const transformTranscript = (
  transcript: PodcastTranscriptItem[] | undefined,
  durationInSeconds: number,
): TransformedTranscript | null => {
  if (!transcript || transcript.length === 0) return null;

  const lines: TranscriptLine[] = transcript.map((item, index) => {
    const currentStartTime = item.startTimeSeconds || 0;
    const nextItem = transcript[index + 1];
    const nextStartTime = nextItem
      ? nextItem.startTimeSeconds
      : durationInSeconds;

    const line: TranscriptLine = {
      start: currentStartTime * 1000, // Convert to milliseconds
      end: (nextStartTime || currentStartTime + 5) * 1000, // If no next item, assume 5 seconds or use audio duration
      text: item.message || "",
      speakerName: item.displayName,
      speakerId: item.id,
    };

    // Calculate word timings for this line
    line.wordTimings = calculateWordTimings(line);

    return line;
  });

  return {
    sections: [
      {
        lines,
      },
    ],
  };
};

// Deprecated local audio sync hook removed after switching to shared player context

// Get dominant color helper
const getDominantColor = async (): Promise<string> => {
  return "#9333ea";
};

// Format time helper
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// Enhanced Transcript Display Component with Spotify-style highlighting
const TranscriptDisplay: React.FC<{
  transcript: TransformedTranscript | null;
  activeLine: TranscriptLine | null;
  activeWordIndex: number;
  currentTime: number;
  onSeekToLine: (time: number) => void;
  dominantColor: string;
}> = ({ transcript, activeLine, activeWordIndex, onSeekToLine }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeLineRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to active line
  useEffect(() => {
    if (activeLine && activeLineRef.current && containerRef.current) {
      const container = containerRef.current;
      const activeElement = activeLineRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = activeElement.getBoundingClientRect();

      const containerCenter = containerRect.height / 2;
      const elementCenter =
        elementRect.top - containerRect.top + elementRect.height / 2;
      const scrollOffset = elementCenter - containerCenter;

      container.scrollTo({
        top: container.scrollTop + scrollOffset,
        behavior: "smooth",
      });
    }
  }, [activeLine]);

  if (!transcript) {
    return (
      <div className="flex items-center justify-center h-full text-white/60">
        <p className="text-lg">No transcript available</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-full overflow-y-auto px-8 py-6 scrollbar-thin scrollbar-thumb-black/20 dark:scrollbar-thumb-white/20 scrollbar-track-black/5 dark:scrollbar-track-white/5"
    >
      <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-8">
        Transcript
      </h3>
      <div className="space-y-6">
        {transcript.sections.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            {section.lines.map((line, lineIndex) => {
              const isActive = activeLine === line;
              const words = line.text.split(" ").filter((w) => w.length > 0);

              return (
                <div
                  key={`${sectionIndex}-${lineIndex}`}
                  ref={isActive ? activeLineRef : null}
                  className={`mb-6 p-4 rounded-lg transition-all duration-300 cursor-pointer transform ${
                    isActive
                      ? "bg-black/5 dark:bg-white/20 scale-105 shadow-lg"
                      : "hover:bg-black/5 dark:hover:bg-white/5 scale-100"
                  }`}
                  onClick={() => onSeekToLine(line.start)}
                >
                  <div className="flex items-center justify-between mb-2">
                    {line.speakerName && (
                      <p
                        className={`text-sm font-semibold transition-colors duration-300 ${
                          isActive
                            ? "text-gray-800 dark:text-white"
                            : "text-gray-500 dark:text-white/40"
                        }`}
                      >
                        {line.speakerName}
                      </p>
                    )}
                    <span
                      className={`text-xs transition-colors duration-300 ${
                        isActive
                          ? "text-gray-600 dark:text-white/80"
                          : "text-gray-400 dark:text-white/30"
                      }`}
                    >
                      {formatTime(line.start / 1000)}
                    </span>
                  </div>
                  <div>
                    <p
                      className={`text-lg leading-relaxed transition-all duration-300 ${
                        isActive
                          ? "text-gray-800 dark:text-white font-medium"
                          : "text-gray-600 dark:text-white/40"
                      }`}
                    >
                      {words.map((word, wordIndex) => (
                        <span
                          key={wordIndex}
                          className={`inline-block transition-all duration-300 ${
                            isActive && wordIndex <= activeWordIndex
                              ? "text-gray-900 dark:text-white"
                              : ""
                          }`}
                          style={{
                            marginRight:
                              wordIndex < words.length - 1 ? "0.25em" : "0",
                          }}
                        >
                          {word}
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Modal Component
const PodcastPlayerModal: React.FC<PodcastPlayerModalProps> = ({
  podcast,
  onClose,
  onRegenClick,
  podcastGenerationType,
  openPdfCustomizationModal,
  initialTimeMs,
  autoPlay,
}) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [dominantColor, setDominantColor] = useState<string>("#9333ea");
  // volume control removed in favor of speed control
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState<string | undefined>(
    podcast?.title,
  );
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>();
  const [showAssignModal, setShowAssignModal] = useState<boolean>(false);

  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const getPodcastsQueryKey = getGetUserPodcastsQueryKey();
  // const customizationSettings = useAppSelector(
  //   (state) => state.user.teacher?.preferences?.pdfOptions,
  // );

  const {
    currentPodcastId,
    isPlaying: ctxIsPlaying,
    currentTimeMs,
    durationMs,
    togglePlayPause,
    seek: ctxSeek,
    // setVolume: ctxSetVolume,
    play: ctxPlay,
    playbackRate,
    setPlaybackRate,
  } = usePodcastPlayer();

  const transformedTranscript = useMemo(() => {
    const audioSeconds = durationMs ? durationMs / 1000 : 0;
    return transformTranscript(podcast?.transcript, audioSeconds);
  }, [podcast?.transcript, durationMs]);

  // Derive active line and active word from context time
  const { activeLine, activeWordIndex } = useMemo(() => {
    const result = {
      activeLine: null as TranscriptLine | null,
      activeWordIndex: -1,
    };
    if (!transformedTranscript) return result;
    const t = currentTimeMs;
    outer: for (const section of transformedTranscript.sections) {
      for (const line of section.lines) {
        if (t >= line.start && t < line.end) {
          result.activeLine = line;
          if (line.wordTimings) {
            for (let i = 0; i < line.wordTimings.length; i++) {
              const w = line.wordTimings[i];
              if (t >= w.start && t < w.end) {
                result.activeWordIndex = i;
                break;
              }
            }
          }
          break outer;
        }
      }
    }
    return result;
  }, [transformedTranscript, currentTimeMs]);

  const deletePodcast = useDeletePodcast({
    mutation: {
      onMutate: async (variables) => {
        const previousData = queryClient.getQueryData(getPodcastsQueryKey);
        // Optimistic update if list is in cache
        queryClient.setQueryData(getPodcastsQueryKey, (oldData: any) => ({
          ...oldData,
          data: oldData?.data?.filter((p: any) => p.id !== variables.id),
        }));
        return { previousData };
      },
      onError: (_err, _vars, context) => {
        queryClient.setQueryData(
          getPodcastsQueryKey,
          (context as any)?.previousData,
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: getPodcastsQueryKey });
      },
    },
  });

  const updatePodcast = useUpdatePodcast({
    mutation: {
      onMutate: async (variables) => {
        const previousData = queryClient.getQueryData(getPodcastsQueryKey);
        queryClient.setQueryData(getPodcastsQueryKey, (oldData: any) => ({
          ...oldData,
          data: oldData?.data?.map((p: any) =>
            p.id === variables.id ? { ...p, title: variables.data.title } : p,
          ),
        }));
        return { previousData };
      },
      onError: (_error, _variables, context) => {
        queryClient.setQueryData(
          getPodcastsQueryKey,
          (context as any)?.previousData,
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: getPodcastsQueryKey });
      },
    },
  });

  const handleMp3Download = useCallback(() => {
    if (!podcast?.url) return;
    const link = document.createElement("a");
    link.href = podcast.url;
    link.download = `${podcast.title || "podcast"}.mp3`;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  }, [podcast?.url, podcast?.title]);

  const handleTitleSubmit = useCallback(async () => {
    if (!podcast?.id || !editedName || editedName === podcast.title) {
      setIsEditing(false);
      return;
    }
    try {
      await updatePodcast.mutateAsync({
        id: podcast.id,
        data: { title: editedName },
      });
      addToast({ title: "", description: "", variant: "success" });
    } catch (_e) {
      addToast({ title: "", description: "", variant: "error" });
    } finally {
      setIsEditing(false);
    }
  }, [podcast?.id, podcast?.title, editedName, updatePodcast, addToast]);

  useEffect(() => {
    if (podcast?.imageUrl) {
      getDominantColor().then((color) => {
        setDominantColor(color);
      });
    }
  }, [podcast?.imageUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      if (typeof initialTimeMs === "number") {
        audio.currentTime = initialTimeMs / 1000;
      }
      if (autoPlay) {
        audio.play().catch(() => {
          /* ignore autoplay blocks */
        });
      }
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [initialTimeMs, autoPlay]);

  // seeking handled inline via ctxSeek

  const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRate = parseFloat(e.target.value);
    setPlaybackRate(newRate);
  };

  const progressPercentage =
    durationMs > 0 ? (currentTimeMs / durationMs) * 100 : 0;

  const modalContent = (
    <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/10 dark:bg-black/80">
      {/* Background gradient */}
      {/* Light theme gradient */}
      <div
        className="absolute inset-0 block dark:hidden"
        style={{
          background: `linear-gradient(to bottom, ${dominantColor}33, ${dominantColor}14, #ffffff)`,
        }}
      />
      {/* Dark theme gradient */}
      <div
        className="absolute inset-0 hidden dark:block"
        style={{
          background: `linear-gradient(to bottom, ${dominantColor}40, ${dominantColor}20, #000000)`,
        }}
      />

      {/* Blurred background image */}
      {podcast?.imageUrl && (
        <div
          className="absolute inset-0 opacity-10 dark:opacity-20"
          style={{
            backgroundImage: `url(${podcast.imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(50px)",
          }}
        />
      )}

      {/* Modal content */}
      <div className="relative w-full h-full max-w-7xl max-h-[90vh] mx-4 rounded-2xl overflow-hidden shadow-2xl bg-white/80 dark:bg-black/40 backdrop-blur-xl flex">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/10 hover:bg-black/20 text-gray-700 transition-colors dark:bg-black/20 dark:hover:bg-black/40 dark:text-white"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Global actions toolbar */}
        <div className="absolute top-4 right-16 z-10 flex items-center gap-2">
          <Button
            onClick={() => setShowAssignModal(true)}
            rounded="xl"
            size="sm"
            bgColor="bg-black/10 hover:bg-black/20 dark:bg-white/10 dark:hover:bg-white/20"
            textColor="text-gray-900 dark:text-white"
            className="border border-black/20 dark:border-white/20 backdrop-blur-sm"
            leftIcon={<Users className="w-4 h-4" />}
          >
            {t("podcast.assignToStudents")}
          </Button>
          {onRegenClick && podcast?.id && podcastGenerationType && (
            <Button
              onClick={() => onRegenClick(podcast.id!, podcastGenerationType)}
              rounded="xl"
              size="sm"
              bgColor="bg-purple-500/20 hover:bg-purple-500/30 dark:bg-purple-500/30 dark:hover:bg-purple-500/40"
              textColor="text-gray-900 dark:text-white"
              className="border border-black/20 dark:border-white/20 backdrop-blur-sm"
              leftIcon={<Wand2 className="w-4 h-4" />}
            >
              {t("assignments.generator.preview.regenerate")}
            </Button>
          )}
        </div>

        {/* Left side - Player */}
        <div className="w-1/2 p-8 flex flex-col justify-center items-center">
          {/* Title & author with inline edit */}
          <div className="w-full mb-6 text-center">
            {!isEditing ? (
              <>
                <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">
                  {podcast?.title}
                </h2>
                <div className="flex items-center justify-center gap-2">
                  <p className="text-lg text-gray-500 dark:text-white/70">
                    {podcast?.authorName}
                  </p>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-2 rounded-md bg-black/10 hover:bg-black/20 transition-colors dark:bg-white/10 dark:hover:bg-white/20"
                  >
                    <Edit3 className="w-5 h-5 text-gray-700 dark:text-white" />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <input
                  value={editedName || ""}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="px-3 py-2 rounded-md bg-black/10 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black/20 w-80 dark:bg-white/10 dark:text-white dark:placeholder-white/60 dark:focus:ring-white/30"
                  placeholder={podcast?.title || t("common.title")}
                />
                <Button
                  onClick={handleTitleSubmit}
                  rounded="lg"
                  size="sm"
                  bgColor="bg-black/10 hover:bg-black/20 dark:bg-white/20 dark:hover:bg-white/30"
                  textColor="text-gray-900 dark:text-white"
                >
                  {t("common.save")}
                </Button>
                <Button
                  onClick={() => setIsEditing(false)}
                  rounded="lg"
                  size="sm"
                  bgColor="bg-black/10 hover:bg-black/20 dark:bg-white/10 dark:hover:bg-white/20"
                  textColor="text-gray-900 dark:text-white"
                >
                  {t("common.cancel")}
                </Button>
              </div>
            )}
          </div>
          {/* Album Cover */}
          {podcast?.imageUrl && (
            <img
              src={podcast.imageUrl}
              alt={podcast.title}
              className="w-80 h-80 rounded-lg shadow-2xl object-cover mb-8"
            />
          )}

          {/* author is rendered above with title */}

          {/* Progress Bar */}
          <div className="w-full max-w-md mb-6">
            <div
              className="h-1 bg-black/10 dark:bg-white/20 rounded-full cursor-pointer relative group"
              onClick={(e) => {
                const rect = (
                  e.currentTarget as HTMLDivElement
                ).getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const percentage = clickX / rect.width;
                const newTime = percentage * (durationMs || 0);
                ctxSeek(newTime);
              }}
            >
              <div
                className="h-full bg-black dark:bg-white rounded-full relative transition-all"
                style={{ width: `${progressPercentage}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-black dark:bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <div className="flex justify-between mt-2 text-sm">
              <span className="text-gray-700 dark:text-white/70">
                {formatTime(currentTimeMs / 1000)}
              </span>
              <span className="text-gray-700 dark:text-white/70">
                {formatTime((durationMs || 0) / 1000)}
              </span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-6 mb-6">
            <button
              onClick={() => ctxSeek(Math.max(0, currentTimeMs - 10000))}
              className="p-2 text-gray-700 hover:text-gray-900 transition-colors dark:text-white/70 dark:hover:text-white"
            >
              <SkipBack className="w-6 h-6" />
            </button>

            <button
              onClick={() => {
                if (currentPodcastId !== podcast?.id) {
                  ctxPlay(
                    { id: podcast?.id, url: podcast?.url },
                    currentTimeMs,
                  );
                } else {
                  togglePlayPause();
                }
              }}
              className="p-4 rounded-full hover:scale-105 transition-transform bg-gray-900 text-white dark:bg-white dark:text-black"
            >
              {currentPodcastId === podcast?.id && ctxIsPlaying ? (
                <Pause className="w-8 h-8" />
              ) : (
                <Play className="w-8 h-8 ml-1" />
              )}
            </button>

            <button
              onClick={() =>
                ctxSeek(Math.min(durationMs || 0, currentTimeMs + 10000))
              }
              className="p-2 text-gray-700 hover:text-gray-900 transition-colors dark:text-white/70 dark:hover:text-white"
            >
              <SkipForward className="w-6 h-6" />
            </button>
          </div>

          {/* Speed Control */}
          <div className="flex items-center gap-3 w-full max-w-xs">
            <span className="text-sm text-gray-700 dark:text-white/70 w-10 text-right">
              {`${playbackRate?.toFixed(1)}x`}
            </span>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={playbackRate}
              onChange={handleSpeedChange}
              className="flex-1 h-2 bg-gray-300 dark:bg-white/20 rounded-full appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/20 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-gray-300 [&::-webkit-slider-thumb]:shadow [&::-webkit-slider-thumb]:cursor-pointer dark:[&::-webkit-slider-thumb]:border-transparent"
            />
          </div>

          {/* Hidden audio element not needed here since shared context manages playback */}

          {/* Downloads */}
          <div className="mt-6 flex items-center gap-3">
            <Button
              onClick={handleMp3Download}
              rounded="xl"
              size="sm"
              bgColor="bg-white hover:bg-gray-100 border border-gray-200 dark:bg-white/10 dark:hover:bg-white/20 dark:border-white/20"
              textColor="text-gray-700 dark:text-white"
              leftIcon={<Download className="w-4 h-4" />}
            >
              {"MP3"}
            </Button>
            {openPdfCustomizationModal && (
              <Button
                onClick={() => openPdfCustomizationModal(podcast?.id)}
                rounded="xl"
                size="sm"
                bgColor="bg-white hover:bg-gray-100 border border-gray-200 dark:bg-white/10 dark:hover:bg-white/20 dark:border-white/20"
                textColor="text-gray-700 dark:text-white"
                leftIcon={<FileText className="w-4 h-4" />}
              >
                {t("common.downloadPdf")}
              </Button>
            )}
            {podcast?.id && (
              <Button
                onClick={() => setShowDeletePopup(podcast?.id)}
                rounded="xl"
                size="sm"
                bgColor="bg-red-500/20 hover:bg-red-500/30 dark:bg-red-500/30 dark:hover:bg-red-500/40"
                textColor="text-gray-900 dark:text-white"
                className="border border-black/20 dark:border-white/20 backdrop-blur-sm"
                leftIcon={<Trash2 className="w-4 h-4" />}
              >
                {t("common.delete")}
              </Button>
            )}
          </div>
        </div>

        {/* Right side - Transcript */}
        <div className="w-1/2 bg-white/60 dark:bg-black/40 backdrop-blur-md border-l border-black/10 dark:border-white/10">
          <TranscriptDisplay
            transcript={transformedTranscript}
            activeLine={activeLine}
            activeWordIndex={activeWordIndex}
            currentTime={currentTimeMs}
            onSeekToLine={(ms) => ctxSeek(ms)}
            dominantColor={dominantColor}
          />
        </div>
      </div>

      {/* Delete Confirm */}
      {showDeletePopup && (
        <DeleteConfirmPopUp
          title={`Delete "${podcast?.title}"?`}
          showDeletePopup={showDeletePopup}
          handleDelete={() => {
            if (!podcast?.id) return;
            deletePodcast.mutateAsync({ id: podcast.id });
            setShowDeletePopup(undefined);
            onClose();
          }}
          setShowDeletePopup={setShowDeletePopup}
        />
      )}

      {/* Assign to Students */}
      {showAssignModal && podcast?.id && (
        <PodcastAssignmentModal
          podcastId={podcast.id}
          podcastTitle={podcast.title}
          show={showAssignModal}
          onClose={() => setShowAssignModal(false)}
        />
      )}
    </div>
  );

  // Render into body via portal so it is always full-screen and not clipped by parents
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    return createPortal(modalContent, document.body);
  }
  return modalContent;
};

export default PodcastPlayerModal;
